import { WEEKDAYS } from "../lib/enums";
import { formatShiftRange } from "../lib/records";

// An employee's work profile (§12) at a glance.
//
// A shift can be partial: the API stores whatever you send, so a record may
// carry `graceMinutes` with no timings at all. That is not the same as having
// no profile — but it also isn't a usable one, because classification needs
// `startTime`/`endTime`. Both cases are shown honestly rather than rendering
// an empty week strip that looks like "works no days".
//
// `workingDays` is weekday numbers, 0 = Sunday … 6 = Saturday.

export function WorkingDayStrip({ workingDays, size = "sm" }) {
  const days = Array.isArray(workingDays) ? workingDays : [];
  const compact = size === "sm";
  return (
    <span className="inline-flex gap-0.5">
      {WEEKDAYS.map((d) => {
        const on = days.includes(d.value);
        return (
          <span
            key={d.value}
            title={`${d.label}${on ? "" : " — off"}`}
            className={`inline-flex items-center justify-center rounded font-semibold ${
              compact ? "h-4 w-4 text-[9px]" : "h-6 w-6 text-[11px]"
            } ${
              on
                ? "bg-emerald-500 text-white"
                : "bg-gray-100 text-gray-400"
            }`}
          >
            {d.label[0]}
          </span>
        );
      })}
    </span>
  );
}

export default function ShiftSummary({ shift, size = "sm" }) {
  const hasTimings = !!(shift?.startTime && shift?.endTime);
  const days = Array.isArray(shift?.workingDays) ? shift.workingDays : [];

  if (!shift || (!hasTimings && days.length === 0)) {
    return (
      <span
        className="text-xs text-gray-400"
        title="Without shift timings a day is never classified automatically, and only recorded days count toward pay."
      >
        No shift set
      </span>
    );
  }

  // A night shift is legitimate: an end before the start runs past midnight.
  const overnight = hasTimings && shift.endTime < shift.startTime;

  return (
    <div className="flex flex-col gap-1">
      {hasTimings ? (
        // Read as spoken — "9 AM to 6 PM". The 24h values the API actually
        // stores are kept on hover for anyone checking an exact boundary.
        <span
          className="flex items-center gap-1 text-xs font-medium text-gray-700"
          title={`Stored as ${shift.startTime}–${shift.endTime}${overnight ? " (ends next day)" : ""}`}
        >
          {formatShiftRange(shift.startTime, shift.endTime)}
          {overnight && (
            <span className="text-[10px] font-normal text-gray-400" title="Runs past midnight">
              +1d
            </span>
          )}
        </span>
      ) : (
        <span className="text-xs text-amber-700" title="Grace or thresholds are set, but no start/end time — days can't be classified until timings are set.">
          Timings not set
        </span>
      )}
      {days.length > 0 ? (
        <WorkingDayStrip workingDays={days} size={size} />
      ) : (
        <span className="text-[10px] text-gray-400">No days chosen</span>
      )}
    </div>
  );
}
